import {
  getSettings,
  getEnabledGoals,
  getEntries,
  getCoachDailyState,
  getUserPreferences,
  getReminderFollowupContext,
  getLongTermMemoryMonthlySnippets,
  upsertSummaryDocCacheBatch,
  formatDate,
  getProChatPermissions,
} from './storage';
import { postCloudAiStream, postCloudAi } from './cloudApi';
import { anonymizeForAi, collectClientAnonymizeHints, isCoachablePersonalNameHint } from './anonymizeForAi';

const cleanText = (value) => String(value || '')
  .replace(/<start_of_turn>|<end_of_turn>|<start_of_image>|<\|im_(start|end)\|>|```|\[END\]|\[INST\]|\[\/INST\]/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * First name for coach prompts and fallbacks. Rejects bracket placeholders and the literal
 * stored word "friend" so we do not train the model on junk names or double-anonymize the name line.
 */
const sanitizeCoachFirstName = (raw) => {
  const rawName = String(raw ?? '').trim();
  if (!rawName) return 'friend';
  const lower = rawName.toLowerCase();
  if (lower === '[user]' || lower === '[name]') return 'friend';
  if (rawName.startsWith('[')) return 'friend';
  if (!isCoachablePersonalNameHint(rawName)) return 'friend';
  const first = rawName.split(/\s+/)[0].trim().slice(0, 40);
  if (!first || !isCoachablePersonalNameHint(first)) return 'friend';
  return first;
};

const getUserName = () => sanitizeCoachFirstName(getSettings()?.name);

const repairModelPlaceholders = (text, displayFirst) => {
  let t = String(text || '');
  const nm = String(displayFirst || '').trim();
  if (nm && nm.toLowerCase() !== 'friend') {
    t = t.replace(/\[(?:user|name)\]/gi, nm);
  } else {
    t = t.replace(/\[(?:user|name)\]\s*/gi, ' ');
  }
  return cleanText(t);
};

const parseCommands = (responseText) => {
  const commandPattern = /\[([A-Z_]+):\s*([^\]]*)\]/g;
  const commands = [];

  for (const match of String(responseText || '').matchAll(commandPattern)) {
    commands.push({ type: match[1], value: String(match[2] ?? '').trim() });
  }

  const text = String(responseText || '').replace(commandPattern, '').replace(/\s+/g, ' ').trim();
  return { text, commands };
};

/** Hide setting-command tokens while streaming; removes complete tags and a trailing partial `[CMD`… */
const stripChatCommandTagsForStream = (raw) => {
  let t = String(raw || '').replace(/\[[A-Z_]+:\s*[^\]]*\]/g, '');
  t = t.replace(/\[[A-Z_]*(?::[^\]]*)?$/g, '').trimEnd();
  t = cleanText(t);
  return repairModelPlaceholders(t, getUserName());
};

const isBadReply = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return true;
  if (text.startsWith('subject:') || text.startsWith('re:') || text.startsWith('fw:')) return true;
  if (text.includes('rebuild-me support')) return true;
  if (text.includes('<start_of_turn>') || text.includes('<end_of_turn>') || text.includes('<start_of_image>')) return true;
  // Coach may cite journal entry keys (YYYY-MM-DD) when linking a memory to a day; do not blanket-reject.
  if (/^\d{4}-\d{2}-\d{2}\s*$/i.test(String(value || '').trim())) return true;
  if (/^\d{4}[\-/]\d{1,2}[\-/]\d{1,2}/.test(text)) return true;
  if (/\b\d+\s+wins?\b/.test(text)) return true;
  if (/\bthe user is\b/.test(text)) return true;
  if (/\byour role is\b/.test(text)) return true;
  if (/\[name\]|\[user\]/i.test(text)) return true;
  if (/\bhere'?s the current state\b/.test(text)) return true;
  if (/\bproject manager\b|\bsoftware development company\b|\blocal community\b/.test(text)) return true;
  if (/\bworld is a canvas\b|\bpaint a brighter hue\b/.test(text)) return true;
  if (text.split('\n').filter(Boolean).length > 12) return true;
  return false;
};

const SLIM_SESSION_TEXT_MAX = 320;

const slimSessionMessages = (sessionMessages) =>
  (Array.isArray(sessionMessages) ? sessionMessages : [])
    .filter((m) => {
      if (!m || (m.role !== 'user' && m.role !== 'assistant')) return false;
      if (String(m.text || '').trim()) return true;
      const a = m.attachment;
      return Boolean(a && (String(a.storagePath || '').trim() || String(a.url || '').trim()));
    })
    .map((m) => {
      let text = String(m.text || '').trim().slice(0, SLIM_SESSION_TEXT_MAX);
      if (!text && m.attachment) text = '[Photo shared]';
      return { role: m.role, text };
    });

const collapseJournalSnippetWs = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

/** Snippet text that is only a generic coach question (no user substance). */
const looksLikeGenericCoachQuestion = (t) => {
  const s = collapseJournalSnippetWs(t).toLowerCase();
  if (s.length > 140) return false;
  return /^how did that make you feel/.test(s)
    || /^what was that like for you\??$/.test(s)
    || /^how are you feeling( right now)?\??$/.test(s)
    || /^can you (say|tell) more about that\??$/.test(s)
    || /^what would help right now\??$/.test(s);
};

const isJournalPlaceholder = (raw) => {
  const t = collapseJournalSnippetWs(raw);
  return t === '–' || t === '-' || t === '—';
};

/**
 * Collect user journal / log text for one day (prompt answers, imported notes, comments, etc.).
 * Order: structured answers → legacy notes → saved highlight → thread comments → photo captions → AI reflection.
 */
const collectEntryLogParts = (entry) => {
  if (!entry || typeof entry !== 'object') return [];
  const parts = [];
  const seen = new Set();
  const push = (raw) => {
    const t = collapseJournalSnippetWs(raw);
    if (!t || isJournalPlaceholder(t)) return;
    const key = t.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    parts.push(t);
  };

  Object.keys(entry)
    .filter((k) => /^answer\d+$/i.test(k))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .forEach((k) => push(entry[k]));

  if (typeof entry.notes === 'string') push(entry.notes);

  push(entry.savedComment);

  if (Array.isArray(entry.comments)) {
    entry.comments.forEach((c) => push(c?.text));
  }

  if (Array.isArray(entry.attachments)) {
    entry.attachments.forEach((att) => push(att?.caption));
  }

  push(entry.aiResponse);

  return parts;
};

/** Past N calendar days (excluding today), most recent first. Line count + body char cap mirror `proCoachServer.js` (COACH_JOURNAL_SNIPPET_MAX_LINES / COACH_JOURNAL_SNIPPET_BODY_MAX_CHARS). */
const JOURNAL_SNIPPET_DAY_COUNT = 7;
/** Per-day body cap after `[YYYY-MM-DD] ` — dynamic coach text is not part of Vertex persona cache (see vertexCoachContextCache.js). */
const JOURNAL_SNIPPET_DAY_CHAR_LIMIT = 280;
const JOURNAL_SNIPPET_MIN_DAY_CHARS = 12;

const buildJournalSnippetLines = () => {
  const entries = getEntries() || {};
  const lines = [];
  for (let delta = 1; delta <= JOURNAL_SNIPPET_DAY_COUNT; delta += 1) {
    const d = new Date();
    d.setDate(d.getDate() - delta);
    const key = formatDate(d);
    const e = entries[key];
    const assembled = collectEntryLogParts(e).join(' · ');
    if (!assembled) continue;
    const clipped =
      assembled.length > JOURNAL_SNIPPET_DAY_CHAR_LIMIT
        ? `${assembled.slice(0, JOURNAL_SNIPPET_DAY_CHAR_LIMIT)}…`
        : assembled;
    if (collapseJournalSnippetWs(clipped).length < JOURNAL_SNIPPET_MIN_DAY_CHARS) continue;
    if (looksLikeGenericCoachQuestion(clipped)) continue;
    lines.push(`[${key}] ${clipped}`);
  }
  return lines.slice(0, JOURNAL_SNIPPET_DAY_COUNT);
};

const buildCoachContextPayload = () => {
  const goals = getEnabledGoals().slice(0, 8).map((g) => ({
    text: String(g.text || '').trim(),
    lastMentionedAt: typeof g.lastMentionedAt === 'string' ? g.lastMentionedAt : '',
  }));
  const coachDay = getCoachDailyState();
  return {
    displayFirstName: getUserName(),
    goals,
    userPreferences: getUserPreferences(),
    coachDailyState: {
      userCoachMessages: Math.max(0, Number(coachDay.userCoachMessages) || 0),
      reflectionOffered: Boolean(coachDay.reflectionOffered),
    },
    reminderFollowups: getReminderFollowupContext(),
    longTermMemorySummaries: getLongTermMemoryMonthlySnippets(3, 300),
    journalSnippets: buildJournalSnippetLines(),
    userLocalHour: new Date().getHours(),
    journalCalendarDay: formatDate(new Date()),
  };
};

const lastAssistantText = (sessionMessages) => {
  const list = Array.isArray(sessionMessages)
    ? sessionMessages.filter((m) => m && (m.role === 'user' || m.role === 'assistant'))
    : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (String(list[i].role || '').toLowerCase() === 'assistant') {
      const t = String(list[i].text || '').trim();
      if (t) return t;
    }
  }
  return '';
};

const repliesTooSimilar = (nextText, prevText) => {
  const a = String(nextText || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const b = String(prevText || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length < 14 || b.length < 14) return a === b;
  const prefixLen = Math.min(28, a.length, b.length);
  if (prefixLen >= 14 && a.slice(0, prefixLen) === b.slice(0, prefixLen)) return true;
  const words = (s) => new Set(s.split(/\s+/).filter((w) => w.length > 2));
  const wa = words(a);
  const wb = words(b);
  let inter = 0;
  for (const w of wa) if (wb.has(w)) inter += 1;
  const uni = wa.size + wb.size - inter;
  return uni >= 4 && inter / uni >= 0.72;
};

export const isProIOSSupported = () => true;

export const summarizeImportedNotes = async (notesText, { onStreamDelta, signal } = {}) => {
  const trimmed = (notesText || '').trim();
  if (!trimmed) return '';

  try {
    let raw = '';
    await postCloudAiStream(
      '/api/ai/import-notes-stream',
      {
        notesText: trimmed,
        max_tokens: 120,
        temperature: 0.2,
      },
      {
        signal,
        onDelta: (d) => {
          raw += d;
          onStreamDelta?.(cleanText(raw));
        },
        onDone: () => {},
        onError: (err) => {
          throw err;
        },
      },
    );
    const result = cleanText(raw);

    if (!result) {
      return `Imported notes captured (${trimmed.length} chars). AI summary is currently unavailable; try again later.`;
    }

    return result;
  } catch (error) {
    console.error('Import summary error:', error);
    return `Imported notes captured (${trimmed.length} chars). AI summary is currently unavailable; try again later.`;
  }
};

export const generateProChatReply = async ({
  userMessage,
  sessionMessages,
  importedSummary = '',
  permissions = null,
  temperature = 0.9,
  onStreamDelta,
  signal,
}) => {
  const userName = getUserName();
  const fullSaved = String(getSettings()?.name || '').trim();
  const nameTokens = new Set(
    [userName, fullSaved, ...fullSaved.split(/\s+/).filter(Boolean)]
      .map((x) => String(x || '').trim().toLowerCase())
      .filter(Boolean),
  );
  const piiHints = collectClientAnonymizeHints().filter((n) => !nameTokens.has(String(n || '').trim().toLowerCase()));

  try {
    try {
      const recent = await postCloudAi('/api/summaries/recent-monthly?limit=3', {});
      if (Array.isArray(recent?.items) && recent.items.length) {
        upsertSummaryDocCacheBatch('monthly', recent.items);
      }
    } catch {
      // Optional memory hydrate only — do not block chat reply.
    }
    const coachContext = buildCoachContextPayload();
    coachContext.displayFirstName = sanitizeCoachFirstName(coachContext.displayFirstName);
    const ragQuery = anonymizeForAi(String(userMessage || '').trim().slice(0, 1200), { extraNames: piiHints });
    const perms = permissions === null || permissions === undefined ? getProChatPermissions() : permissions;

    const coachBody = {
      user_name: fullSaved || userName || '',
      userMessage: String(userMessage || '').trim().slice(0, 1200),
      sessionMessages: slimSessionMessages(sessionMessages),
      coachContext,
      importedSummary: String(importedSummary || '').trim().slice(0, 480),
      permissions: perms,
      /** Hard cap for coach reply length (aligned with product preference for concise turns). */
      max_tokens: 300,
      temperature: typeof temperature === 'number' && Number.isFinite(temperature)
        ? Math.min(1, Math.max(0, temperature))
        : 1,
      enableJournalRag: true,
      ragQuery,
    };

    let rawModelText = '';
    let usageEstimate = 0;

    await postCloudAiStream(
      '/api/ai/coach-stream',
      coachBody,
      {
        signal,
        onDelta: (d) => {
          rawModelText += d;
          onStreamDelta?.(stripChatCommandTagsForStream(rawModelText));
        },
        onDone: (payload) => {
          usageEstimate = Number(payload?.usageEstimate) || 0;
        },
        onError: (err) => {
          throw err;
        },
      },
    );

    if (!usageEstimate) {
      usageEstimate = Math.ceil((JSON.stringify(coachBody).length + rawModelText.length) / 3.5);
    }

    const resultData = { text: rawModelText };
    const { text: parsedText, commands } = parseCommands(cleanText(resultData?.text || ''));
    let cleanedText = cleanText(parsedText);
    cleanedText = repairModelPlaceholders(cleanedText, userName);

    const prevCoach = lastAssistantText(sessionMessages);
    const tooSimilar = repliesTooSimilar(cleanedText, prevCoach);

    if (isBadReply(cleanedText) || tooSimilar) {
      const fallback = tooSimilar
        ? `I hear you, ${userName}. What part of that feels most important to unpack next?`
        : `Thanks for sharing that, ${userName}. If you want to go deeper, point to one piece and we will keep it small.`;
      onStreamDelta?.(fallback);
      return {
        text: fallback,
        commands: [],
        usageEstimate: 0,
      };
    }

    return {
      text: cleanedText,
      commands,
      usageEstimate,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('Chat reply error:', msg, error);
    const errText = `Something hiccuped on my side, ${userName}. Try again in a moment—or send one shorter line.`;
    onStreamDelta?.(errText);
    return {
      text: errText,
      commands: [],
      usageEstimate: 0,
    };
  }
};

export default {
  isProIOSSupported,
  summarizeImportedNotes,
  generateProChatReply,
};
